'use client';
import React, { useState, useEffect, useRef } from 'react';
import EditableText from './EditableText';
import EditableImage from './EditableImage';
import EditableAudio from './EditableAudio';
import { ChevronLeft, ChevronRight, X, Eye } from 'lucide-react';
import { useEditMode } from './EditModeProvider';
import ColorPicker from './ColorPicker';
import PhoneInput from './ui/PhoneInput';
import axios from 'axios';
import { useSmartCaptcha } from '../lib/useSmartCaptcha';
import { isCompletePhone, toE164 } from '../lib/phoneMask';

const SolutionFeatures = () => {
  const { isEditMode, getContent } = useEditMode();
  const { getCaptchaToken } = useSmartCaptcha();
  const [activeTab, setActiveTab] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [tab2Slide, setTab2Slide] = useState(0);
  const [feedbackName, setFeedbackName] = useState('');
  const [feedbackPhone, setFeedbackPhone] = useState('');
  const [feedbackAgree, setFeedbackAgree] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState({ loading: false, error: null, success: false });

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackAgree) {
      setFeedbackStatus({ loading: false, error: 'Необходимо согласие с условиями', success: false });
      return;
    }
    if (!isCompletePhone(feedbackPhone)) {
      setFeedbackStatus({ loading: false, error: 'Введите телефон полностью', success: false });
      return;
    }

    setFeedbackStatus({ loading: true, error: null, success: false });
    try {
      const smartToken = await getCaptchaToken();
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000/api';
      await axios.post(`${apiUrl}/leads`, { name: feedbackName, phone: toE164(feedbackPhone), smartToken });

      setFeedbackStatus({ loading: false, error: null, success: true });
      setFeedbackName('');
      setFeedbackPhone('');
      setFeedbackAgree(false);

      setTimeout(() => {
        setFeedbackStatus(s => ({ ...s, success: false }));
      }, 5000);
    } catch (error) {
      console.error('Submit error:', error);
      setFeedbackStatus({
        loading: false,
        error: 'Произошла ошибка при отправке заявки. Пожалуйста, попробуйте позже.',
        success: false
      });
    }
  };
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [tab4Slide, setTab4Slide] = useState(0);

  // Состояние для остановки авто-переключения при ручном взаимодействии
  const [isAutoPlayStopped, setIsAutoPlayStopped] = useState(false);

  const tabsContainerRef = useRef(null);

  const colorSettings = [
    { key: 'sol_bg_light', label: 'Фон секции (светлый)', default: '#ffffff' },
    { key: 'sol_bg_dark', label: 'Фон секции (темный)', default: '#0A0A0A' },
    { key: 'sol_content_bg_light', label: 'Фон контента (светлый)', default: '#F9FAFB' },
    { key: 'sol_content_bg_dark', label: 'Фон контента (темный)', default: '#111827' },
    { key: 'sol_tab_active_bg', label: 'Активная вкладка', default: '#17c6a6' },
    { key: 'sol_tab_inactive_bg_light', label: 'Неактивная вкладка (светлая)', default: '#ffffff' },
    { key: 'sol_tab_inactive_bg_dark', label: 'Неактивная вкладка (темная)', default: '#111827' },
    { key: 'sol_feedback_bg', label: 'Фон формы обратной связи', default: 'linear-gradient(101.46deg, #13547A -7.74%, #80D0C7 132.4%)' },
  ];

  const tabs = [
    { id: 0, titleKey: 'sol_tab_title_0', defaultTitle: 'Мониторинг оборудования и помещений', iconKey: 'sol_tab_icon_0' },
    { id: 1, titleKey: 'sol_tab_title_1', defaultTitle: 'Автоматические журналы', iconKey: 'sol_tab_icon_1' },
    { id: 2, titleKey: 'sol_tab_title_2', defaultTitle: 'Оповещения при тревоге', iconKey: 'sol_tab_icon_2' },
    { id: 3, titleKey: 'sol_tab_title_3', defaultTitle: 'Формирование задач', iconKey: 'sol_tab_icon_3' },
    { id: 4, titleKey: 'sol_tab_title_4', defaultTitle: 'Создание отчетов', iconKey: 'sol_tab_icon_4' },
  ];

  const handleTabClick = (id, isManual = true) => {
    setActiveTab(id);
    if (isManual) {
      setIsAutoPlayStopped(true);
    }
  };

  // Логика автоматического последовательного переключения табов (для десктопа)
  useEffect(() => {
    if (isAutoPlayStopped) return;
    
    // Проверка на десктопную версию
    if (typeof window !== 'undefined' && window.innerWidth < 768) return;

    let timer;

    if (activeTab === 0) {
      if (currentSlide < 4) {
        timer = setTimeout(() => {
          setCurrentSlide((prev) => prev + 1);
        }, 5000);
      } else {
        timer = setTimeout(() => {
          setCurrentSlide(0);
          handleTabClick(1, false);
        }, 6000);
      }
    } else if (activeTab === 1) {
      timer = setTimeout(() => {
        handleTabClick(2, false);
      }, 7000);
    } else if (activeTab === 2) {
      if (tab2Slide < 2) {
        timer = setTimeout(() => {
          setTab2Slide((prev) => prev + 1);
        }, 5000);
      } else {
        timer = setTimeout(() => {
          setTab2Slide(0);
          handleTabClick(3, false);
        }, 6000);
      }
    } else if (activeTab === 3) {
      timer = setTimeout(() => {
        handleTabClick(4, false);
      }, 7000);
    }
    // На activeTab === 4 авто-переключение завершается

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [activeTab, currentSlide, tab2Slide, isAutoPlayStopped]);

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev === 4 ? 0 : prev + 1));
    setIsAutoPlayStopped(true);
  };

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? 4 : prev - 1));
    setIsAutoPlayStopped(true);
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
    setIsAutoPlayStopped(true);
  };

  const handleNextTab2Slide = () => {
    setTab2Slide((prev) => (prev === 2 ? 0 : prev + 1));
    setIsAutoPlayStopped(true);
  };

  const handlePrevTab2Slide = () => {
    setTab2Slide((prev) => (prev === 0 ? 2 : prev - 1));
    setIsAutoPlayStopped(true);
  };

  const handleTab2DotClick = (index) => {
    setTab2Slide(index);
    setIsAutoPlayStopped(true);
  };

  const handleNextMobileTab = () => {
    const nextIndex = (activeTab + 1) % tabs.length;
    handleTabClick(nextIndex, true);
    if (tabsContainerRef.current && tabsContainerRef.current.children[nextIndex]) {
      tabsContainerRef.current.children[nextIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  <EditableText contentKey={`sol_tab0_main_title`} defaultValue="Мониторинг оборудования и помещений 24/7" />
                </h2>
                <p className="text-gray-600 dark:text-gray-400 text-lg">
                  <EditableText contentKey={`sol_tab0_main_desc`} defaultValue="Обеспечьте непрерывный контроль температурного режима и микроклимата во всех точках вашего предприятия." />
                </p>
                <div className="space-y-2">
                   <div 
                      className={`flex items-start space-x-4 p-4 -mx-4 rounded-2xl cursor-pointer transition-all duration-300 border ${currentSlide === 0 || currentSlide === 1 ? 'bg-teal-50/80 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800/50 shadow-sm' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                      onClick={(e) => { 
                          if (isEditMode && e.target.closest('input, textarea, button, [contenteditable="true"]')) return;
                          setCurrentSlide(0); 
                          setIsAutoPlayStopped(true); 
                      }}
                   >
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab0_icon_1`} defaultSrc="https://api.iconify.design/lucide:thermometer.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:thermometer.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                           <EditableText contentKey={`sol_tab0_item1_title`} defaultValue={`Холодильное оборудование`} />
                         </h3>
                         <p className="text-gray-600 dark:text-gray-400">
                           <EditableText contentKey={`sol_tab0_item1_desc`} defaultValue={`Автоматический контроль температурного режима в холодильниках и морозильниках с точностью до 0.1°C.`} />
                         </p>
                      </div>
                   </div>
                   <div 
                      className={`flex items-start space-x-4 p-4 -mx-4 rounded-2xl cursor-pointer transition-all duration-300 border ${currentSlide === 2 || currentSlide === 3 ? 'bg-teal-50/80 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800/50 shadow-sm' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                      onClick={(e) => { 
                          if (isEditMode && e.target.closest('input, textarea, button, [contenteditable="true"]')) return;
                          setCurrentSlide(2); 
                          setIsAutoPlayStopped(true); 
                      }}
                   >
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab0_icon_3`} defaultSrc="https://api.iconify.design/lucide:thermometer-sun.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:thermometer-sun.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                           <EditableText contentKey={`sol_tab0_item3_title`} defaultValue={`Контроль микроклимата`} />
                         </h3>
                         <p className="text-gray-600 dark:text-gray-400">
                           <EditableText contentKey={`sol_tab0_item3_desc`} defaultValue={`Мониторинг температуры и влажности в производственных и складских помещениях.`} />
                         </p>
                      </div>
                   </div>
                   <div 
                      className={`flex items-start space-x-4 p-4 -mx-4 rounded-2xl cursor-pointer transition-all duration-300 border ${currentSlide === 4 ? 'bg-teal-50/80 dark:bg-teal-900/30 border-teal-200 dark:border-teal-800/50 shadow-sm' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
                      onClick={(e) => { 
                          if (isEditMode && e.target.closest('input, textarea, button, [contenteditable="true"]')) return;
                          setCurrentSlide(4); 
                          setIsAutoPlayStopped(true); 
                      }}
                   >
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab0_icon_2`} defaultSrc="https://api.iconify.design/lucide:lamp.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:lamp.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                           <EditableText contentKey={`sol_tab0_item2_title`} defaultValue={`Облучатели и рециркуляторы`} />
                         </h3>
                         <p className="text-gray-600 dark:text-gray-400">
                           <EditableText contentKey={`sol_tab0_item2_desc`} defaultValue={`Учет времени наработки бактерицидных ламп и контроль эффективности обеззараживания воздуха.`} />
                         </p>
                      </div>
                   </div>
                </div>
            </div>
            <div className="relative flex justify-center items-center">
              <button onClick={handlePrevSlide} className="absolute left-0 z-30 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow hover:bg-white dark:hover:bg-gray-700 transition">
                <ChevronLeft className="w-6 h-6 text-gray-800 dark:text-white" />
              </button>
              
              <div className="relative w-[275px] h-[560px] sm:w-[320px] sm:h-[650px] bg-black rounded-[46px] sm:rounded-[55px] shadow-2xl border-[10px] sm:border-[12px] border-black overflow-hidden flex-shrink-0 ring-1 ring-gray-800">
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-7 bg-black rounded-full w-32 z-20 flex items-center justify-between px-3">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#111] shadow-inner"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-900/30"></div>
                </div>
                <div className="relative w-full h-full bg-white dark:bg-gray-900 overflow-hidden rounded-[36px] sm:rounded-[43px] group">
                  <div className="flex w-full h-full transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                    {[1, 2, 3, 4, 5].map((slide) => (
                      <div key={slide} className="w-full h-full flex-shrink-0 relative overflow-hidden">
                         <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-110">
                            <EditableImage contentKey={`sol_tab0_slide_${slide}`} defaultSrc={`https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=320&h=650&text=Slide+${slide}`} defaultValue={`https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=320&h=650&text=Slide+${slide}`} alt={`Slide ${slide}`} className="w-full h-full object-cover" />
                         </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <button onClick={handleNextSlide} className="absolute right-0 z-30 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow hover:bg-white dark:hover:bg-gray-700 transition">
                <ChevronRight className="w-6 h-6 text-gray-800 dark:text-white" />
              </button>
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
                {[0, 1, 2, 3, 4].map((dot) => (
                  <button key={dot} onClick={() => handleDotClick(dot)} className={`w-2.5 h-2.5 rounded-full transition-colors ${currentSlide === dot ? 'bg-[var(--tab-active-bg)]' : 'bg-gray-300 dark:bg-gray-600'}`} />
                ))}
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                <EditableText contentKey={`sol_tab1_main_title`} defaultValue="Автоматическое ведение журналов учета" />
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                <EditableText contentKey={`sol_tab1_main_desc`} defaultValue="Система автоматически фиксирует все показания датчиков и формирует необходимые журналы в реальном времени, исключая человеческий фактор." />
              </p>
              <div className="space-y-6">
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab1_icon_1`} defaultSrc="https://api.iconify.design/lucide:clock.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:clock.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab1_li1_title`} defaultValue="Экономия времени сотрудников" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab1_li1_desc`} defaultValue="Освободите персонал от рутинного ручного заполнения бумажных отчетов." /></p>
                      </div>
                   </div>
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab1_icon_2`} defaultSrc="https://api.iconify.design/lucide:check-circle-2.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:check-circle-2.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab1_li2_title`} defaultValue="Исключение ошибок" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab1_li2_desc`} defaultValue="Все данные поступают напрямую с датчиков, что гарантирует их достоверность." /></p>
                      </div>
                   </div>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-2xl border border-blue-100 dark:border-blue-800">
                 <h4 className="font-bold text-blue-900 dark:text-blue-100 mb-2"><EditableText contentKey={`sol_tab1_box_title`} defaultValue="Соответствие нормам СанПиН" /></h4>
                 <p className="text-blue-700 dark:text-blue-300 text-sm"><EditableText contentKey={`sol_tab1_box_desc`} defaultValue="Журналы полностью соответствуют требованиям контролирующих органов и готовы к проверкам." /></p>
              </div>
            </div>
            <div className="flex justify-center">
               <div className="relative w-[275px] h-[560px] sm:w-[320px] sm:h-[650px] bg-black rounded-[46px] sm:rounded-[55px] shadow-2xl border-[10px] sm:border-[12px] border-black overflow-hidden flex-shrink-0 ring-1 ring-gray-800 group">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 h-7 bg-black rounded-full w-32 z-20 flex items-center justify-between px-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#111] shadow-inner"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-900/30"></div>
                  </div>
                  <div className="relative w-full h-full bg-white dark:bg-gray-900 overflow-hidden rounded-[36px] sm:rounded-[43px]">
                     <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-110">
                        <EditableImage contentKey={`sol_tab1_main_img`} defaultSrc="https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&q=80&w=320&h=650" defaultValue="https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&q=80&w=320&h=650" alt="App Screen" className="w-full h-full object-cover" />
                     </div>
                  </div>
               </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                <EditableText contentKey={`sol_tab2_main_title`} defaultValue="Мгновенные уведомления о нарушениях" />
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                <EditableText contentKey={`sol_tab2_main_desc`} defaultValue="При выходе параметров за пределы нормы система моментально оповестит ответственных сотрудников." />
              </p>
              <div className="space-y-6">
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab2_icon_1`} defaultSrc="https://api.iconify.design/lucide:bell-ring.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:bell-ring.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab2_li1_title`} defaultValue="Push и SMS уведомления" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab2_li1_desc`} defaultValue="Получайте тревожные сигналы на мобильный телефон в течение нескольких секунд." /></p>
                      </div>
                   </div>
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab2_icon_2`} defaultSrc="https://api.iconify.design/lucide:siren.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:siren.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab2_li2_title`} defaultValue="Эскалация инцидентов" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab2_li2_desc`} defaultValue="Если дежурный не отреагировал, уведомление автоматически отправляется руководству." /></p>
                      </div>
                   </div>
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab2_icon_3`} defaultSrc="https://api.iconify.design/lucide:phone-call.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:phone-call.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <div className="flex flex-wrap items-center gap-3 mb-1">
                             <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab2_li3_title`} defaultValue="Звонок" /></h4>
                             <EditableAudio contentKey="sol_tab2_audio" defaultSrc="/demo-call.wav" label="прослушать демо" />
                         </div>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab2_li3_desc`} defaultValue="Автоматический голосовой звонок ответственным лицам при критических нарушениях." /></p>
                      </div>
                   </div>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-6 rounded-2xl border border-red-100 dark:border-red-800">
                 <h4 className="font-bold text-red-900 dark:text-red-100 mb-2"><EditableText contentKey={`sol_tab2_box_title`} defaultValue="Минимизация рисков" /></h4>
                 <p className="text-red-700 dark:text-red-300 text-sm"><EditableText contentKey={`sol_tab2_box_desc`} defaultValue="Предотвращайте порчу продукции и простои оборудования за счет быстрой реакции." /></p>
              </div>
            </div>
            <div className="relative flex justify-center items-center">
              <button onClick={handlePrevTab2Slide} className="absolute left-0 z-30 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow hover:bg-white dark:hover:bg-gray-700 transition">
                <ChevronLeft className="w-6 h-6 text-gray-800 dark:text-white" />
              </button>

               <div className="relative w-[275px] h-[560px] sm:w-[320px] sm:h-[650px] bg-black rounded-[46px] sm:rounded-[55px] shadow-2xl border-[10px] sm:border-[12px] border-black overflow-hidden flex-shrink-0 ring-1 ring-gray-800">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 h-7 bg-black rounded-full w-32 z-20 flex items-center justify-between px-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#111] shadow-inner"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-900/30"></div>
                  </div>
                  <div className="relative w-full h-full bg-white dark:bg-gray-900 overflow-hidden rounded-[36px] sm:rounded-[43px] group">
                     <div className="flex w-full h-full transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${tab2Slide * 100}%)` }}>
                        <div className="w-full h-full flex-shrink-0 relative overflow-hidden">
                           <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-110">
                              <EditableImage contentKey={`sol_tab2_main_img`} defaultSrc="https://images.unsplash.com/photo-1596742578443-7682ef5251cd?auto=format&fit=crop&q=80&w=320&h=650" defaultValue="https://images.unsplash.com/photo-1596742578443-7682ef5251cd?auto=format&fit=crop&q=80&w=320&h=650" alt="App Screen 1" className="w-full h-full object-cover" />
                           </div>
                        </div>
                        <div className="w-full h-full flex-shrink-0 relative overflow-hidden">
                           <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-110">
                              <EditableImage contentKey={`sol_tab2_slide_2`} defaultSrc="https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&q=80&w=320&h=650" defaultValue="https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&q=80&w=320&h=650" alt="App Screen 2" className="w-full h-full object-cover" />
                           </div>
                        </div>
                        <div className="w-full h-full flex-shrink-0 relative overflow-hidden">
                           <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-110">
                              <EditableImage contentKey={`sol_tab2_slide_3`} defaultSrc="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=320&h=650" defaultValue="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=320&h=650" alt="App Screen 3" className="w-full h-full object-cover" />
                           </div>
                        </div>
                     </div>
                  </div>
               </div>

              <button onClick={handleNextTab2Slide} className="absolute right-0 z-30 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow hover:bg-white dark:hover:bg-gray-700 transition">
                <ChevronRight className="w-6 h-6 text-gray-800 dark:text-white" />
              </button>
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
                {[0, 1, 2].map((dot) => (
                  <button key={dot} onClick={() => handleTab2DotClick(dot)} className={`w-2.5 h-2.5 rounded-full transition-colors ${tab2Slide === dot ? 'bg-[var(--tab-active-bg)]' : 'bg-gray-300 dark:bg-gray-600'}`} />
                ))}
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                <EditableText contentKey={`sol_tab3_main_title`} defaultValue="Автоматическое управление задачами" />
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                <EditableText contentKey={`sol_tab3_main_desc`} defaultValue="Система сама создает задачи для персонала при возникновении инцидентов или по расписанию." />
              </p>
              <div className="space-y-6">
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab3_icon_1`} defaultSrc="https://api.iconify.design/lucide:clipboard-list.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:clipboard-list.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab3_li1_title`} defaultValue="Контроль исполнения" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab3_li1_desc`} defaultValue="Отслеживайте статус выполнения задач и время реакции сотрудников." /></p>
                      </div>
                   </div>
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab3_icon_2`} defaultSrc="https://api.iconify.design/lucide:calendar-clock.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:calendar-clock.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab3_li2_title`} defaultValue="Шаблоны регламентных работ" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab3_li2_desc`} defaultValue="Автоматическое планирование регулярного технического обслуживания." /></p>
                      </div>
                   </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-2xl border border-green-100 dark:border-green-800">
                 <h4 className="font-bold text-green-900 dark:text-green-100 mb-2"><EditableText contentKey={`sol_tab3_box_title`} defaultValue="Прозрачность процессов" /></h4>
                 <p className="text-green-700 dark:text-green-300 text-sm"><EditableText contentKey={`sol_tab3_box_desc`} defaultValue="Каждое действие фиксируется в системе, что позволяет оценить эффективность работы команды." /></p>
              </div>
            </div>
            <div className="flex justify-center">
               <div className="relative w-[275px] h-[560px] sm:w-[320px] sm:h-[650px] bg-black rounded-[46px] sm:rounded-[55px] shadow-2xl border-[10px] sm:border-[12px] border-black overflow-hidden flex-shrink-0 ring-1 ring-gray-800 group">
                  <div className="absolute top-2 left-1/2 -translate-x-1/2 h-7 bg-black rounded-full w-32 z-20 flex items-center justify-between px-3">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#111] shadow-inner"></div>
                      <div className="w-2.5 h-2.5 rounded-full bg-blue-900/30"></div>
                  </div>
                  <div className="relative w-full h-full bg-white dark:bg-gray-900 overflow-hidden rounded-[36px] sm:rounded-[43px]">
                     <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-110">
                        <EditableImage contentKey={`sol_tab3_main_img`} defaultSrc="https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&q=80&w=320&h=650" defaultValue="https://images.unsplash.com/photo-1611162616475-46b635cb6868?auto=format&fit=crop&q=80&w=320&h=650" alt="App Screen" className="w-full h-full object-cover" />
                     </div>
                  </div>
               </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                <EditableText contentKey={`sol_tab4_main_title`} defaultValue="Аналитические отчеты в один клик" />
              </h2>
              <p className="text-gray-600 dark:text-gray-400 text-lg">
                <EditableText contentKey={`sol_tab4_main_desc`} defaultValue="Формируйте детальные отчеты о работе оборудования и соблюдении режимов за любой период." />
              </p>
              <div className="space-y-6">
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab4_icon_1`} defaultSrc="https://api.iconify.design/lucide:file-spreadsheet.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:file-spreadsheet.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab4_li1_title`} defaultValue="Экспорт в Excel и PDF" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab4_li1_desc`} defaultValue="Выгружайте готовые таблицы для предоставления руководству или инспекторам." /></p>
                      </div>
                   </div>
                   <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 flex-shrink-0">
                         <EditableImage contentKey={`sol_tab4_icon_2`} defaultSrc="https://api.iconify.design/lucide:trending-up.svg?color=%2300797a" defaultValue="https://api.iconify.design/lucide:trending-up.svg?color=%2300797a" alt="Icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                         <h4 className="font-bold text-gray-900 dark:text-white"><EditableText contentKey={`sol_tab4_li2_title`} defaultValue="Графики и тренды" /></h4>
                         <p className="text-sm text-gray-500 dark:text-gray-400"><EditableText contentKey={`sol_tab4_li2_desc`} defaultValue="Визуальный анализ изменения параметров для прогнозирования сбоев." /></p>
                      </div>
                   </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                 <div className="bg-indigo-50 dark:bg-indigo-900/20 p-6 rounded-2xl border border-indigo-100 dark:border-indigo-800">
                    <h4 className="font-bold text-indigo-900 dark:text-indigo-100 mb-2"><EditableText contentKey="sol_tab4_box1_title" defaultValue="Отчеты в Excel" /></h4>
                    <p className="text-indigo-700 dark:text-indigo-300 text-sm"><EditableText contentKey="sol_tab4_box1_desc" defaultValue="Удобное скачивание структурированных данных." /></p>
                 </div>
                 <div className="bg-teal-50 dark:bg-teal-900/20 p-6 rounded-2xl border border-teal-100 dark:border-teal-800">
                    <h4 className="font-bold text-teal-900 dark:text-teal-100 mb-2"><EditableText contentKey="sol_tab4_box2_title" defaultValue="Невозможно потерять" /></h4>
                    <p className="text-teal-700 dark:text-teal-300 text-sm"><EditableText contentKey="sol_tab4_box2_desc" defaultValue="Все отчеты хранятся в защищенном архиве и доступны 24/7." /></p>
                 </div>
              </div>
            </div>
            <div className="flex justify-center relative">
               <div className="relative w-full max-w-[600px] bg-gray-100 dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden group cursor-pointer" onClick={() => setIsLightboxOpen(true)}>
                  <div className="flex items-center px-4 py-2 bg-gray-200 dark:bg-gray-900 space-x-2">
                     <div className="w-3 h-3 rounded-full bg-red-400"></div>
                     <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                     <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                  <div className="relative w-full aspect-[16/10] overflow-hidden bg-white dark:bg-gray-900">
                     <div className="flex w-full h-full transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${tab4Slide * 100}%)` }}>
                        <div className="w-full h-full flex-shrink-0 relative overflow-hidden">
                           <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-105">
                              <EditableImage contentKey="sol_tab4_main_img" defaultSrc="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&h=500" defaultValue="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800&h=500" alt="Mac Screen 1" className="w-full h-full object-cover" />
                           </div>
                        </div>
                        <div className="w-full h-full flex-shrink-0 relative overflow-hidden">
                           <div className="w-full h-full transform transition-transform duration-700 group-hover:scale-105">
                              <EditableImage contentKey="sol_tab4_slide_2" defaultSrc="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800&h=500" defaultValue="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800&h=500" alt="Mac Screen 2" className="w-full h-full object-cover" />
                           </div>
                        </div>
                     </div>
                     <div className="absolute inset-0 bg-transparent group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                         <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-4 rounded-full bg-black/20 backdrop-blur-sm">
                             <Eye className="w-10 h-10 text-white" />
                         </div>
                     </div>
                  </div>
               </div>
               
               <button 
                  onClick={(e) => { e.stopPropagation(); setTab4Slide(0); }}
                  disabled={tab4Slide === 0}
                  className={`absolute -left-4 top-1/2 -translate-y-1/2 z-30 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition ${tab4Slide === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
               >
                  <ChevronLeft className="w-6 h-6 text-gray-800 dark:text-white" />
               </button>
               <button 
                  onClick={(e) => { e.stopPropagation(); setTab4Slide(1); }}
                  disabled={tab4Slide === 1}
                  className={`absolute -right-4 top-1/2 -translate-y-1/2 z-30 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition ${tab4Slide === 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
               >
                  <ChevronRight className="w-6 h-6 text-gray-800 dark:text-white" />
               </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <section className="py-20 sol-section-bg transition-colors duration-300 relative">
      <style>{`
        .sol-section-bg {
          background: ${getContent('sol_bg_light', '#ffffff')};
        }
        .dark .sol-section-bg {
          background: ${getContent('sol_bg_dark', '#0A0A0A')};
        }
        .sol-content-bg {
          background: ${getContent('sol_content_bg_light', '#F9FAFB')};
        }
        .dark .sol-content-bg {
          background: ${getContent('sol_content_bg_dark', '#111827')};
        }
        .sol-tab-inactive {
          background: ${getContent('sol_tab_inactive_bg_light', '#ffffff')};
        }
        .dark .sol-tab-inactive {
          background: ${getContent('sol_tab_inactive_bg_dark', '#111827')};
        }
        :root {
          --tab-active-bg: ${getContent('sol_tab_active_bg', '#17c6a6')};
        }
        .sol-feedback-bg-custom {
          background: ${getContent('sol_feedback_bg', 'linear-gradient(101.46deg, #13547A -7.74%, #80D0C7 132.4%)')} !important;
        }
      `}</style>

      <ColorPicker settings={colorSettings} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-4xl font-extrabold text-gray-900 dark:text-white mb-10 text-center lg:text-left">
          <EditableText contentKey="sol_section_title" defaultValue="Возможности решения" />
        </h2>

        {/* Tabs */}
        <div className="relative group/tabs w-full mb-12">
          <div ref={tabsContainerRef} className="flex flex-nowrap overflow-x-auto [&::-webkit-scrollbar]:hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-4 w-full">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`relative min-w-[240px] sm:min-w-[260px] md:min-w-0 flex-shrink-0 md:flex-shrink flex items-center space-x-3 px-4 py-4 rounded-xl border-2 transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'text-white shadow-lg border-transparent'
                    : 'sol-tab-inactive border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:shadow-md'
                }`}
                style={activeTab === tab.id ? { backgroundColor: 'var(--tab-active-bg)' } : {}}
              >
                <div className={`relative z-10 w-8 h-8 flex-shrink-0 ${activeTab === tab.id ? 'brightness-0 invert' : ''}`}>
                  <EditableImage contentKey={tab.iconKey} defaultSrc={`https://api.iconify.design/lucide:layout-dashboard.svg?color=%2300797a`} defaultValue={`https://api.iconify.design/lucide:layout-dashboard.svg?color=%2300797a`} alt="icon" className="w-full h-full object-contain" />
                </div>
                <span className="relative z-10 font-medium whitespace-normal break-words text-left text-sm leading-snug max-w-[180px] sm:max-w-none">
                  <EditableText contentKey={tab.titleKey} defaultValue={tab.defaultTitle} />
                </span>
              </button>
            ))}
          </div>
          
          <button 
            onClick={handleNextMobileTab} 
            className="md:hidden absolute right-2 top-1/2 -translate-y-1/2 z-30 p-2.5 rounded-full bg-[#00797A] text-white shadow-lg animate-pulse ring-4 ring-[#00797A]/30 transition-all active:scale-95"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Content Area */}
        <div className="sol-content-bg rounded-[40px] p-8 lg:p-12 border border-gray-100 dark:border-gray-800 transition-colors duration-300">
          {renderTabContent()}
        </div>

        {/* Feedback Form integrated at the bottom */}
        <div className="mt-12 sol-feedback-bg-custom rounded-3xl p-8 lg:p-12 text-white shadow-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 rounded-full bg-white opacity-5 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 rounded-full bg-[#00e5e6] opacity-10 blur-3xl"></div>
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4">
                    <EditableText contentKey="sol_feedback_title" defaultValue="Поможем разобраться" />
                  </h3>
                  <p className="text-teal-50 text-lg">
                    <EditableText contentKey="sol_feedback_desc" defaultValue="Если у вас есть вопросы по функционалу системы, наш менеджер перезвонит и ответит на них в течение 20 минут" />
                  </p>
              </div>
              <form className="space-y-5" onSubmit={handleFeedbackSubmit}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <input type="text" placeholder="Имя" value={feedbackName} onChange={(e) => setFeedbackName(e.target.value)} className="w-full px-5 py-4 rounded-xl text-gray-900 bg-white outline-none focus:ring-2 focus:ring-teal-300 transition-shadow" required />
                      <PhoneInput value={feedbackPhone} onChange={setFeedbackPhone} pattern="\+7 \(\d{3}\) \d{3}-\d{4}" title="Введите телефон полностью" className="w-full px-5 py-4 rounded-xl text-gray-900 bg-white outline-none focus:ring-2 focus:ring-teal-300 transition-shadow" required />
                  </div>
                  <label className="flex items-start space-x-3 text-sm text-teal-100 cursor-pointer">
                      <input type="checkbox" checked={feedbackAgree} onChange={(e) => setFeedbackAgree(e.target.checked)} className="mt-1 rounded text-teal-500 focus:ring-teal-500 w-4 h-4" required />
                      <span className="leading-tight">
                        Я принимаю условия{' '}
                        <a href="https://ses-rb.ru/polzovatelskoe-soglashenie/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">пользовательского соглашения</a>,{' '}
                        <a href="https://ses-rb.ru/politika-konfidentsialnosti/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">политики конфиденциальности</a> и{' '}
                        <a href="https://ses-rb.ru/soglasiye-na-obrabotku-personalnyh-dannyh/" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">согласия на обработку персональных данных</a>
                      </span>
                  </label>
                  <button type="submit" disabled={feedbackStatus.loading} className="bg-[#17c6a6] hover:bg-[#14ab90] text-white font-bold py-4 px-8 rounded-xl transition-colors w-full sm:w-auto shadow-lg disabled:opacity-70">
                      {feedbackStatus.loading ? 'Отправка...' : 'Перезвоните мне'}
                  </button>
                  {feedbackStatus.success && (
                      <div className="p-4 bg-green-500/20 border border-green-400 text-white rounded-xl text-center">
                          Ваша заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.
                      </div>
                  )}
                  {feedbackStatus.error && (
                      <div className="p-4 bg-red-500/20 border border-red-400 text-white rounded-xl text-center">
                          {feedbackStatus.error}
                      </div>
                  )}
              </form>
          </div>
        </div>

      </div>

      {/* Lightbox for Tab 4 */}
      {isLightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsLightboxOpen(false)}>
          <button onClick={() => setIsLightboxOpen(false)} className="absolute top-6 right-6 text-white/70 hover:text-white transition">
            <X className="w-10 h-10" />
          </button>
          <div className="relative max-w-7xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
             {tab4Slide === 0 ? (
                 <EditableImage contentKey="sol_tab4_main_img" defaultSrc="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200&h=800" defaultValue="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200&h=800" alt="Full Screen" className="w-full h-full object-contain rounded-xl" />
             ) : (
                 <EditableImage contentKey="sol_tab4_slide_2" defaultSrc="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200&h=800" defaultValue="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200&h=800" alt="Full Screen 2" className="w-full h-full object-contain rounded-xl" />
             )}
          </div>
        </div>
      )}
    </section>
  );
};

export default SolutionFeatures;